import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useMsal } from "@azure/msal-react";
import { createPortal } from 'react-dom';
import { useUser } from '../contexts/UserContext';
import { Link } from 'react-router-dom';
import { ApiUser } from '../services/apiService';
import {
    getAllUsers,
    getUserPermissions,
    addUserPermission,
    removeUserPermission,
    UserWithPermissions,
    UserPermission
} from '../services/adminApiService';
import { RoleDto, getRoles, setUserRoles, setUserEntity } from '../services/roleApiService';
import { EntityDto, getEntities } from '../services/entityApiService';
import { 
    Search, 
    Bell, 
    ChevronRight, 
    Map, 
    Database,
    Store,
    Truck,
    CreditCard,
    History, 
    UserCog, 
    Trash2, 
    Plus, 
    Shield,
    MoreHorizontal,
    Folder,
    MapPinned,
    ChevronDown,
    Users
} from 'lucide-react';

const CustomSelect = ({ 
    value, 
    options, 
    onChange,
    className = ""
  }: { 
    value: string, 
    options: { label: string, value: any }[], 
    onChange: (val: any) => void,
    className?: string
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
    const selectedOption = options.find(o => o.value === value);
  
    const toggleOpen = () => {
      if (!isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setCoords({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
      setIsOpen(!isOpen);
    };
  
    return (
      <div className={`relative shrink-0 ${className}`}>
        <button
          ref={buttonRef}
          type="button"
          onClick={toggleOpen}
          className="w-full flex items-center justify-between px-3 h-11 bg-gray-100 dark:bg-slate-800 border-none rounded-lg text-sm font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-primary/50 transition-all"
        >
          <span className="truncate">{selectedOption?.label || value}</span>
          <ChevronDown size={16} className="text-gray-500" />
        </button>
  
        {isOpen && (
          createPortal(
            <>
              <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
              <div 
                className="fixed z-[9999] bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 shadow-xl overflow-hidden animate-in zoom-in-95 duration-200"
                style={{ top: coords.top, left: coords.left, width: coords.width }}
              >
              {options.map(op => (
                <button
                  key={op.value}
                  type="button"
                  onClick={() => {
                    onChange(op.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium transition-colors ${
                    op.value === value 
                      ? 'bg-primary/10 text-primary font-bold' 
                      : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {op.label}
                </button>
              ))}
            </div>
            </>,
            document.body
          )
        )}
      </div>
    );
  };

export function UsersAdminPage() {
    const { isAdmin } = useUser();
    const { instance, accounts } = useMsal();

    // State for Users List
    const [users, setUsers] = useState<ApiUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [userFilter, setUserFilter] = useState<'All' | 'Admins' | 'Standard'>('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

    // State for Selected User Permissions
    const [selectedUserWithPerms, setSelectedUserWithPerms] = useState<UserWithPermissions | null>(null);
    const [permsLoading, setPermsLoading] = useState(false);

    // State for Add Permission Form
    const [newPermType, setNewPermType] = useState('Branch');
    const [newPermValue, setNewPermValue] = useState('');
    const [isAdding, setIsAdding] = useState(false);

    // Roles & entities
    const [allRoles, setAllRoles] = useState<RoleDto[]>([]);
    const [allEntities, setAllEntities] = useState<EntityDto[]>([]);
    const [savingAssignment, setSavingAssignment] = useState(false);
    const [assignmentMsg, setAssignmentMsg] = useState<string | null>(null);
    const [roleSearch, setRoleSearch] = useState('');

    useEffect(() => {
        if (!isAdmin || !accounts[0]) return;
        getRoles(instance, accounts[0]).then(setAllRoles).catch(console.error);
        getEntities(instance, accounts[0]).then(setAllEntities).catch(console.error);
    }, [isAdmin, instance, accounts]);

    const toggleRole = async (roleId: number) => {
        if (!selectedUserWithPerms) return;
        const current = selectedUserWithPerms.roleIds || [];
        const next = current.includes(roleId) ? current.filter(r => r !== roleId) : [...current, roleId];
        setSavingAssignment(true);
        setAssignmentMsg(null);
        try {
            await setUserRoles(instance, accounts[0], selectedUserWithPerms.id, next);
            setSelectedUserWithPerms({ ...selectedUserWithPerms, roleIds: next });
        } catch {
            setAssignmentMsg("Failed to update roles.");
        } finally {
            setSavingAssignment(false);
        }
    };

    const changeEntity = async (entityId: number | null) => {
        if (!selectedUserWithPerms) return;
        setSavingAssignment(true);
        setAssignmentMsg(null);
        try {
            await setUserEntity(instance, accounts[0], selectedUserWithPerms.id, entityId);
            setSelectedUserWithPerms({ ...selectedUserWithPerms, entityId });
        } catch {
            setAssignmentMsg("Failed to update entity.");
        } finally {
            setSavingAssignment(false);
        }
    };

    // Load Users on Mount
    useEffect(() => {
        if (isAdmin && accounts[0]) {
            setUsersLoading(true);
            getAllUsers(instance, accounts[0])
                .then(data => {
                    setUsers(data);
                    if (data.length > 0 && !selectedUserId) {
                        setSelectedUserId(data[0].id);
                    }
                })
                .catch(console.error)
                .finally(() => setUsersLoading(false));
        }
    }, [isAdmin, instance, accounts]);

    // Load Permissions when Selected User Changes
    useEffect(() => {
        if (selectedUserId && accounts[0]) {
            setPermsLoading(true);
            getUserPermissions(instance, accounts[0], selectedUserId)
                .then(data => setSelectedUserWithPerms(data))
                .catch(console.error)
                .finally(() => setPermsLoading(false));
        }
    }, [selectedUserId, instance, accounts]);

    // Filter Users
    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const matchesSearch = 
                user.displayName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                user.email.toLowerCase().includes(searchQuery.toLowerCase());
            
            if (!matchesSearch) return false;

            if (userFilter === 'Admins') return user.userRole === 'Admin';
            if (userFilter === 'Standard') return user.userRole !== 'Admin';
            
            return true;
        });
    }, [users, searchQuery, userFilter]);

    const handleAddPermission = async () => {
        if (!selectedUserId || !newPermValue) return;
        
        setIsAdding(true);
        const newPermission: UserPermission = {
            userId: selectedUserId,
            permissionType: newPermType,
            permissionValue: newPermValue
        };

        try {
            await addUserPermission(instance, accounts[0], newPermission);
            setSelectedUserWithPerms(prev => prev ? {
                ...prev,
                userPermissions: [...prev.userPermissions, newPermission]
            } : null);
            setNewPermValue('');
        } catch (err) {
            console.error("Error adding permission", err);
            alert("Failed to add permission");
        } finally {
            setIsAdding(false);
        }
    };

    const handleRemovePermission = async (permission: UserPermission) => {
        if (!window.confirm(`Remove ${permission.permissionType}: ${permission.permissionValue}?`)) return;
        try {
            await removeUserPermission(instance, accounts[0], permission);
            setSelectedUserWithPerms(prev => prev ? {
                ...prev,
                permissions: prev.userPermissions.filter(p => 
                    p.permissionType !== permission.permissionType || p.permissionValue !== permission.permissionValue
                )
            } : null);
        } catch (err) {
            console.error("Error removing permission.", err);
            alert("Failed to remove permission.");
        }
    };

    const getInitials = (name: string) => {
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    const getPermissionIcon = (type: string) => {
        switch(type.toLowerCase()) {
            case 'branch': return <MapPinned size={18} className="text-primary" />;
            case 'warehouse': return <Database size={18} className="text-primary" />;
            default: return <Shield size={18} className="text-primary" />;
        }
    };

    if (!isAdmin) {
        return (
            <div className="p-8 text-center">
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">Access Denied</h1>
                <p className="text-slate-500 mt-2">You do not have permission to view this page.</p>
                <Link to="/" className="text-primary hover:underline mt-4 block">Go to Dashboard</Link>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full bg-background-light dark:bg-background-dark overflow-hidden font-display text-[#0d121b] dark:text-white transition-colors duration-200">
            <header className="flex h-16 items-center justify-between border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 shrink-0 z-20">
                <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white">
                        <Users size={20} />
                    </div>
                    <h2 className="text-lg font-bold tracking-tight">Users</h2>
                </div>
            </header>

            <div className="flex flex-1 overflow-hidden">
                {/* Left Pane: User Directory */}
                <section className="w-80 border-r border-[#e7ebf3] dark:border-slate-800 bg-white dark:bg-[#101622] flex flex-col shrink-0">
                    <div className="p-4 border-b border-[#e7ebf3] dark:border-slate-800">
                        {/* <h3 className="font-bold text-sm mb-3 text-slate-900 dark:text-white">User Directory</h3> */}
                        <div className="relative mb-3">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input 
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search users..."
                                className="w-full bg-gray-100 dark:bg-slate-800 border-none rounded-lg py-2 pl-9 pr-3 text-xs font-medium focus:ring-2 focus:ring-primary/50 placeholder:text-gray-400 text-slate-900 dark:text-white"
                            />
                        </div>
                        <div className="flex gap-2">
                            {(['All', 'Admins', 'Standard'] as const).map(filter => (
                                <button 
                                    key={filter}
                                    onClick={() => setUserFilter(filter)}
                                    className={`flex-1 text-xs font-bold py-1.5 rounded transition-colors ${
                                        userFilter === filter 
                                        ? 'bg-primary text-white' 
                                        : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700'
                                    }`}
                                >
                                    {filter}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {usersLoading ? (
                            <div className="p-8 text-center text-gray-400 text-sm">Loading users...</div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="p-8 text-center text-gray-400 text-sm">No users found.</div>
                        ) : (
                            filteredUsers.map(user => {
                                const isActive = selectedUserId === user.id;
                                return (
                                    <div 
                                        key={user.id}
                                        onClick={() => setSelectedUserId(user.id)}
                                        className={`flex items-center gap-3 px-4 py-4 cursor-pointer border-b border-[#e7ebf3] dark:border-slate-800 transition-all ${
                                            isActive 
                                            ? 'bg-primary/5 border-l-4 border-l-primary' 
                                            : 'hover:bg-gray-50 dark:hover:bg-slate-800/50 border-l-4 border-l-transparent'
                                        }`}
                                    >
                                        <div className={`size-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isActive ? 'bg-primary text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300'}`}>
                                            {getInitials(user.displayName)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start">
                                                <p className={`text-sm font-bold truncate ${isActive ? 'text-primary' : 'text-slate-900 dark:text-white'}`}>{user.displayName}</p>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${
                                                    user.userRole === 'Admin' 
                                                    ? 'bg-primary/20 text-primary' 
                                                    : 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
                                                }`}>
                                                    {user.userRole === 'Admin' ? 'Admin' : 'User'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </section>

                {/* Right Pane: Permission Details */}
                <section className="flex-1 flex flex-col bg-background-light dark:bg-background-dark overflow-y-auto">
                    {selectedUserWithPerms ? (
                        <div className="p-8">
                            {/* Roles & Entity Assignment */}
                            <div className="bg-white dark:bg-[#1a2130] p-6 rounded-xl border border-[#e7ebf3] dark:border-slate-800 mb-8 shadow-sm">
                                <h4 className="text-sm font-bold mb-4 uppercase tracking-widest text-gray-400">Roles & Entity</h4>
                                <div className="mb-5">
                                    <label className="text-xs font-bold text-gray-500 block mb-2">Entity (visibility cap)</label>
                                    <CustomSelect
                                        value={String(selectedUserWithPerms.entityId ?? "")}
                                        onChange={(v) => changeEntity(v === "" ? null : Number(v))}
                                        options={[
                                            { label: '— None —', value: "" },
                                            ...allEntities.map(e => ({ label: e.name, value: String(e.id) })),
                                        ]}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 block mb-2">Roles</label>
                                    {allRoles.length === 0 ? (
                                        <div className="text-xs text-slate-400 italic">No roles defined.</div>
                                    ) : (
                                        <>
                                        <div className="relative mb-3">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                            <input
                                                type="text"
                                                value={roleSearch}
                                                onChange={(e) => setRoleSearch(e.target.value)}
                                                placeholder="Search roles..."
                                                className="w-full bg-gray-100 dark:bg-slate-800 border-none rounded-lg py-2 pl-9 pr-3 text-xs font-medium focus:ring-2 focus:ring-primary/50 placeholder:text-gray-400 text-slate-900 dark:text-white"
                                            />
                                        </div>
                                        <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                                            {allRoles
                                                .filter(r => {
                                                    const active = (selectedUserWithPerms.roleIds || []).includes(r.id);
                                                    return active || r.name.toLowerCase().includes(roleSearch.toLowerCase());
                                                })
                                                .map(r => {
                                                const active = (selectedUserWithPerms.roleIds || []).includes(r.id);
                                                return (
                                                    <button
                                                        key={r.id}
                                                        onClick={() => toggleRole(r.id)}
                                                        disabled={savingAssignment}
                                                        className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${
                                                            active
                                                                ? 'bg-primary text-white border-primary'
                                                                : 'bg-gray-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-transparent hover:bg-gray-200 dark:hover:bg-slate-700'
                                                        }`}
                                                    >
                                                        {r.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        </>
                                    )}
                                </div>
                                {assignmentMsg && <div className="mt-3 text-xs text-rose-500">{assignmentMsg}</div>}
                            </div>

                                                        {/* Add Permission Form */}
                            {/* <div className="bg-white dark:bg-[#1a2130] p-6 rounded-xl border border-[#e7ebf3] dark:border-slate-800 mb-8 shadow-sm">
                                <h4 className="text-sm font-bold mb-4 uppercase tracking-widest text-gray-400">Add New Permission</h4>
                                <div className="flex flex-wrap gap-4 items-end">
                                    <div className="flex-1 min-w-[200px] flex flex-col gap-2">
                                        <label className="text-xs font-bold text-gray-500">Permission Type</label>
                                        <CustomSelect 
                                            value={newPermType}
                                            onChange={setNewPermType}
                                            options={[
                                                { label: 'Branch Access', value: 'Branch' },
                                                { label: 'Warehouse Access', value: 'Warehouse' },
                                            ]}
                                        />
                                    </div>
                                    <div className="flex-[2] min-w-[300px] flex flex-col gap-2">
                                        <label className="text-xs font-bold text-gray-500">Value</label>
                                        <input 
                                            value={newPermValue}
                                            onChange={(e) => setNewPermValue(e.target.value)}
                                            className="bg-gray-100 dark:bg-slate-800 border-none rounded-lg h-11 text-sm focus:ring-2 focus:ring-primary/50 text-slate-900 dark:text-white px-3" 
                                            placeholder="e.g. CLT, 101, read_write" 
                                            type="text"
                                        />
                                    </div>
                                    <button 
                                        onClick={handleAddPermission}
                                        disabled={isAdding || !newPermValue}
                                        className="h-11 px-8 bg-primary text-white font-bold rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Plus size={18} />
                                        Add Permission
                                    </button>
                                </div>
                            </div> */}

                            {/* Permissions Table */}
                            {/* <div className="bg-white dark:bg-[#1a2130] rounded-xl border border-[#e7ebf3] dark:border-slate-800 shadow-sm overflow-hidden">
                                <div className="px-6 py-4 border-b border-[#e7ebf3] dark:border-slate-800 flex justify-between items-center">
                                    <h4 className="text-sm font-bold uppercase tracking-widest text-gray-400">Current Permissions</h4>
                                    <span className="bg-gray-100 dark:bg-slate-800 text-[10px] font-bold px-2 py-1 rounded text-slate-600 dark:text-slate-300">{selectedUserWithPerms.userPermissions.length} TOTAL</span>
                                </div>
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-gray-50 dark:bg-slate-800/50">
                                        <tr>
                                            <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase">Type</th>
                                            <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase">Value</th>
                                            <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                                        {selectedUserWithPerms.userPermissions.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} className="px-6 py-8 text-center text-gray-400 text-sm">No permissions assigned.</td>
                                            </tr>
                                        ) : (
                                            selectedUserWithPerms.userPermissions.map((perm, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-800/30 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2 text-slate-900 dark:text-white">
                                                            {getPermissionIcon(perm.permissionType)}
                                                            <span className="text-sm font-medium">{perm.permissionType}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <code className="text-xs bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded text-primary font-bold">{perm.permissionValue}</code>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <button 
                                                            onClick={() => handleRemovePermission(perm)}
                                                            className="text-gray-400 hover:text-rose-500 transition-colors p-1 rounded hover:bg-rose-50 dark:hover:bg-rose-500/10"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div> */}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                            <UserCog size={48} className="mb-4 opacity-20" />
                            <p>Select a user from the directory to manage permissions.</p>
                        </div>
                    )}

                    
                </section>
            </div>
        </div>
    );
}